/*
 * Hazy Grove — per-variant image gallery.
 *
 * Drives the markup in snippets/hg-variant-gallery.liquid: a main stage whose
 * active image is chosen by the thumbnail strip, and a lightbox that reuses
 * Dawn's `.product-media-modal` CSS without being a `<product-modal>` element
 * (so it never relocates to document.body and stays swap-safe).
 *
 * Each `<hg-variant-gallery>` is recreated when the surrounding
 * `#HgVariantGallery-<section>` wrapper is swapped on variant change, so all
 * wiring happens in connectedCallback and re-binds for free. trapFocus /
 * removeTrapFocus are the globals defined in assets/global.js.
 */
if (!customElements.get('hg-variant-gallery')) {
  customElements.define(
    'hg-variant-gallery',
    class HgVariantGallery extends HTMLElement {
      connectedCallback() {
        this.mainButtons = Array.from(this.querySelectorAll('.hg-vgallery__main-btn'));
        this.thumbs = Array.from(this.querySelectorAll('.hg-vgallery__thumb'));
        this.lightbox = this.querySelector('.hg-vgallery__lightbox');
        this.lightboxItems = this.lightbox
          ? Array.from(this.lightbox.querySelectorAll('.hg-vgallery__lightbox-item'))
          : [];
        this.activeIndex = 0;

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.setActive(this.indexOf(thumb)));
        });

        this.mainButtons.forEach((button) => {
          button.addEventListener('click', () => this.openLightbox(this.indexOf(button)));
        });

        if (this.lightbox) {
          this.lightbox.querySelectorAll('.hg-vgallery__close').forEach((closeButton) => {
            closeButton.addEventListener('click', () => this.closeLightbox());
          });

          // Match Dawn's media-modal zoom-out behavior: a mouse click on the
          // backdrop or an image closes the lightbox.
          this.lightbox.addEventListener('pointerup', (event) => {
            if (event.pointerType === 'mouse') this.closeLightbox();
          });

          this.lightbox.addEventListener('keyup', (event) => {
            if (event.code && event.code.toUpperCase() === 'ESCAPE') this.closeLightbox();
          });
        }

        this.markImagesLoaded();
      }

      indexOf(element) {
        return parseInt(element.dataset.hgIndex, 10) || 0;
      }

      setActive(index) {
        this.activeIndex = index;
        this.mainButtons.forEach((button) => {
          button.classList.toggle('is-active', this.indexOf(button) === index);
          button.tabIndex = this.indexOf(button) === index ? 0 : -1;
        });
        this.thumbs.forEach((thumb) => {
          const isActive = this.indexOf(thumb) === index;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      openLightbox(index) {
        if (!this.lightbox) return;
        this.lightboxItems.forEach((item) => {
          item.classList.toggle('active', this.indexOf(item) === index);
        });
        this.openedBy = document.activeElement;
        this.lightbox.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        if (typeof trapFocus === 'function') {
          trapFocus(this.lightbox, this.lightbox.querySelector('.product-media-modal__dialog'));
        }
        const activeItem = this.lightboxItems[index];
        if (activeItem) activeItem.scrollIntoView({ block: 'start' });
      }

      closeLightbox() {
        if (!this.lightbox || !this.lightbox.hasAttribute('open')) return;
        this.lightbox.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.openedBy);
        }
      }

      // Fade each image in once it loads; the CSS skeleton shimmer shows until
      // then. Re-runs on every connect, so images swapped in on a variant change
      // get wired too.
      markImagesLoaded() {
        const images = this.querySelectorAll(
          '.hg-vgallery__main-img, .hg-vgallery__thumb-img, .hg-vgallery__lightbox-item img'
        );
        images.forEach((img) => {
          if (img.complete && img.naturalWidth > 0) {
            this.flagLoaded(img);
          } else {
            const done = () => this.flagLoaded(img);
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          }
        });
      }

      flagLoaded(img) {
        img.classList.add('is-loaded');
        const holder = img.closest('.hg-vgallery__main-btn, .hg-vgallery__thumb');
        if (holder) holder.classList.add('hg-img-loaded');
      }
    }
  );
}
